import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spin, Empty, Image, Tag, message } from 'antd';
import { ReloadOutlined, EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { productsApi } from '../../utils/api';

/**
 * Bibliothèque des visuels disponibles pour un produit.
 *
 * Regroupe les images du produit et celles générées par IA (toutes rattachées au
 * produit lors de leur enregistrement). Permet de choisir celle qui sera utilisée
 * par l'annonce, sans relancer de génération — donc sans coût.
 *
 * @param {string}   productId    - Produit promu par l'ensemble de publicités
 * @param {string}   imageActuelle - URL du visuel actuellement appliqué à l'annonce
 * @param {Function} onSelect     - Reçoit l'URL du visuel choisi
 */
const BibliothequeVisuels = ({ productId, imageActuelle, onSelect }) => {
  const [images, setImages] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [apercu, setApercu] = useState(null);

  /** Charge les visuels du produit : images téléversées et images générées par IA */
  const chargerImages = useCallback(async () => {
    if (!productId) return;

    try {
      setChargement(true);
      const reponse = await productsApi.getById(productId);
      const produit = reponse?.data;
      if (!produit) return;

      // Les images générées sont présentées en premier : ce sont les plus récentes
      const generees = (produit.generatedImages || []).map(i => ({
        url: i.image_url,
        origine: 'ia',
        prompt: i.prompt || null
      }));
      const produites = (produit.images || []).map(i => ({
        url: i.image_url,
        origine: 'produit'
      }));

      // Dédoublonnage sur l'URL, en conservant l'ordre
      const vues = new Set();
      setImages([...generees, ...produites].filter(img => {
        if (!img.url || vues.has(img.url)) return false;
        vues.add(img.url);
        return true;
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des visuels du produit:', error);
      message.error('Impossible de charger les visuels du produit');
    } finally {
      setChargement(false);
    }
  }, [productId]);

  useEffect(() => {
    chargerImages();
  }, [chargerImages]);

  if (!productId) {
    return <Empty description="Aucun produit associé à cet ensemble de publicités" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">
          Cliquez sur un visuel pour l'appliquer à cette annonce.
        </span>
        <Button size="small" icon={<ReloadOutlined />} onClick={chargerImages} loading={chargement}>
          Actualiser
        </Button>
      </div>

      {chargement && images.length === 0 ? (
        <div className="text-center py-8"><Spin /></div>
      ) : images.length === 0 ? (
        <Empty description="Aucun visuel pour ce produit. Générez-en un dans l'onglet suivant." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {images.map(image => {
            const appliquee = image.url === imageActuelle;

            return (
              <div
                key={image.url}
                className={`relative rounded overflow-hidden border-2 cursor-pointer group transition ${
                  appliquee ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => onSelect(image.url)}
                title={image.prompt || undefined}
              >
                <img src={image.url} alt="Visuel produit" className="w-full aspect-square object-cover" />

                {/* Agrandir : au-dessus du reste, sans déclencher la sélection */}
                <div
                  className="absolute top-1 right-1 z-20 bg-black bg-opacity-60 rounded p-1 opacity-0 group-hover:opacity-100 transition"
                  onClick={(e) => { e.stopPropagation(); setApercu(image.url); }}
                  title="Voir en grand"
                >
                  <EyeOutlined className="text-white" />
                </div>

                {/* Origine du visuel, pour distinguer les images IA des photos produit */}
                <Tag
                  color={image.origine === 'ia' ? 'purple' : 'default'}
                  className="absolute top-1 left-1 z-10 text-[10px] leading-4 m-0"
                >
                  {image.origine === 'ia' ? 'IA' : 'produit'}
                </Tag>

                {appliquee && (
                  <span className="absolute bottom-0 inset-x-0 z-10 bg-blue-600 text-white text-[10px] text-center py-0.5">
                    <CheckOutlined /> utilisé
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Aperçu plein écran, piloté par l'icône œil */}
      {apercu && (
        <Image
          src={apercu}
          style={{ display: 'none' }}
          preview={{
            visible: true,
            src: apercu,
            onVisibleChange: (visible) => { if (!visible) setApercu(null); }
          }}
        />
      )}
    </div>
  );
};

export default BibliothequeVisuels;
